/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import zipWith from 'lodash/zipWith';

import { isNonEmptyArray } from '../../util.type';
import { mask } from '../bitfield';
import CommunicatorUtility from '../communicator/utility/communicator-utility';
import { dayOfWeekArray, SomeDaysOfWeek } from '../day-of-week';
import { compare, isLess, max } from '../iso-time-string';

const { parseBoolean, parseDaysOfWeek, validateWeekTimesArray } =
  CommunicatorUtility;

/**
 * @import { Power, PowerTimer, WeekDayInterval, RawWeekDayInterval } from '.'
 * @import { NonEmptyArray } from '../../util.type'
 */

/**
 * Maximální počet časovačů, který je dovolen nastavit pro všechny platformy.
 * Na SSSP je počet omezen na 7 časovačů zapnutí a 7 časovačů vypnutí.
 * Na webOS je situace podobná a TV nedovolí nastavit více časovačů 7 + 7.
 * Při pokusu o nastavení dalšího časovače dojde k chybě při volání metody.
 */
export const timerMax = 7;

/**
 * Zpracuje seznam časovačů.
 * @param {WeekDayInterval[]} intervals Naparsované a zvalidované pole intervalů
 * zapnutí pro daný den v týdnu. Očekává se seřazené nejprve podle dayOfWeek,
 * poté podle start času, bez kolizí a překrytí.
 * @returns {PowerTimer[]} Časovače seřazené v ON/OFF dvojicích.
 */
export function mapTimers(intervals) {
  return intervals.flatMap((interval) => {
    const { dayOfWeek: day } = interval;
    return [
      { state: 'on', day, time: interval.start },
      { state: 'off', day, time: interval.end }
    ];
  });
}

/**
 * Sloučí časové intervaly pro každý den v týdnu.
 * @param {RawWeekDayInterval[]} intervals Zvalidované časovače.
 * @returns {RawWeekDayInterval[]} Sloučené časové intervaly pro každý den v týdnu,
 * seřazené podle `dayOfWeek` (od 0) a `start`.
 */
export function mergeAndSortIntervals(intervals) {
  if (intervals.length === 0) {
    return [];
  }
  return (
    intervals
      // Seřadíme podle začátku intervalu a dne v týdnu, jako premisa
      // pro porovnávání a slučování časových intervalů.
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek || compare(a.start, b.start))
      .reduce((acc, curr) => {
        if (acc.length > 0) {
          const prev = acc[acc.length - 1];
          if (
            curr.dayOfWeek === prev.dayOfWeek &&
            !isLess(prev.end, curr.start)
          ) {
            // Pokud se překrývají, sloučíme je.
            prev.end = max(curr.end, prev.end);
            prev.reverseOne ||= curr.reverseOne;
            return acc;
          }
        }
        // Pokud se nepřekrývají, přidáme nový interval.
        acc.push(curr);
        return acc;
      }, /** @type {RawWeekDayInterval[]} */ ([]))
  );
}

/**
 * Vytvoří sjednocení všech dnů v týdnu z časových intervalů.
 * @param {NonEmptyArray<WeekDayInterval>} intervals
 * @returns {SomeDaysOfWeek} Bitová maska dnů týdne, kde jsou definovány
 * intervaly.
 */
function onDaysUnion(intervals) {
  return SomeDaysOfWeek.from(
    mask(...intervals.map(({ dayOfWeek }) => dayOfWeek))
  );
}

/**
 * Zpracuje seznam vypínacích úloh a časovačů.
 *
 * Pokud vráti hodnotu `undefined`, znamená to režim 24/7 (zařízení je vždy zapnuto), kde
 * nejsou definovány žádné časovače ani zpětné vypnutí.
 * @param {Object[]} jsonArray Část odpovědi na obsah zařízení se
 * seznamem všech (nejen shutdown) úloh.
 * @returns {Power | undefined}
 */
export function parse(jsonArray) {
  const intervals = mergeAndSortIntervals(
    jsonArray.filter(({ type }) => type === 'shutdown').flatMap(parseTask)
  );
  // Pokud nejsou žádné časovače, je režim 24/7.
  // Tím pádem není definováno ani zpětné vypnutí a ani dny v týdnu.
  if (!isNonEmptyArray(intervals)) {
    return undefined;
  }
  const onDays = onDaysUnion(intervals);
  return {
    onDays,
    reverseOne: intervals.some(({ reverseOne }) => reverseOne),
    weekDayIntervals: /** @type {NonEmptyArray<WeekDayInterval>} */ (
      intervals.map(({ reverseOne, ...rest }) => rest)
    )
  };
}

/**
 * Zpracuje vypínací úlohu a časovače.
 * @param {Object} json Část odpovědi na obsah zařízení s jednou úlohou.
 * @returns {RawWeekDayInterval[]}
 * @throws {TypeError} Pokud je struktura nevalidní.
 */
function parseTask(json) {
  try {
    const parsedDaysOfWeek = parseDaysOfWeek(json.daysOfWeek);
    // Ki-Wi Server posílá de facto vypínací úlohy a sémantiku start a end
    // v daném dni chápe z pohledu začátku/konce vypnutí (což je přesně naopak,
    // pokud by se to chápalo jako interval zapnutí, který musí být v rámci
    // jedinného dne - pro interval vypnutí je to pak onakvé, že to začíná
    // jeden den a končí kdo ví jaký den). Pro očekávanější názvy a sémantiku
    // bude odsud níže pracovat jako s intervaly zapnutí a zde přemapujeme.
    const startArray = validateWeekTimesArray(json.endTimes);
    const endArray = validateWeekTimesArray(json.startTimes);
    const reverseOne = parseBoolean(json.reverseOne) || false;

    /** @type {RawWeekDayInterval[]} */
    const parsedIntervals = zipWith(
      dayOfWeekArray,
      startArray,
      endArray,
      (dayOfWeek, start, end) =>
        /** @satisfies {RawWeekDayInterval} */ ({
          dayOfWeek,
          start,
          end,
          reverseOne
        })
    );
    // Validace a ošetření chybných intervalů pomocí filtrování (přeskočení).
    // Je nutné pro fungování holiday, aby neexistovaly timery o délce 0,
    // které se v platformním kódu vyruší, takže de facto daný den by nedošlo
    // k zapnutí, což by mělo fatální důsledky. Vedlejším efektem se nově
    // přeskočí i invertované časy, což Ki-Wi Server nyní nedovolí uložit
    // a odeslat (ale historicky to šlo a bylo "nějak" podporováno v Player
    // Lighter).
    return parsedIntervals.filter(
      ({ dayOfWeek, start, end }) =>
        parsedDaysOfWeek.get(dayOfWeek) && isLess(start, end)
    );
  } catch (error) {
    throw new TypeError(
      `parsing week day timers from control task ${JSON.stringify(
        json
      )}: ${error}`
    );
  }
}

export const internal = {
  onDaysUnion,
  parseTask
};
